import { useRef, useState } from "react";
import { Upload, X, Camera, Loader2 } from "lucide-react";
import { api, photoUrl } from "@/lib/api";

export default function PhotoUpload({ photos, setPhotos, max = 6 }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const onFiles = async (files) => {
    setError("");
    const list = Array.from(files).slice(0, max - photos.length);
    if (list.length === 0) return;
    setUploading(true);
    try {
      for (const f of list) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("label", "");
        const { data } = await api.post("/photos/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setPhotos((prev) => [...prev, data]);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = (id) => setPhotos((p) => p.filter((x) => x.id !== id));

  return (
    <div data-testid="photo-upload-section">
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
        multiple
        hidden
        data-testid="photo-upload-input"
        onChange={(e) => onFiles(e.target.files)}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading || photos.length >= max}
        data-testid="photo-upload-trigger-button"
        className="cj-dropzone w-full border-2 border-dashed border-white/15 hover:border-[#ff6b00]/60 rounded-2xl p-8 flex flex-col items-center justify-center gap-2 transition disabled:opacity-50"
      >
        {uploading ? (
          <>
            <Loader2 className="h-7 w-7 text-[#ff6b00] animate-spin" />
            <div className="font-display uppercase font-bold text-slate-200">Uploading...</div>
          </>
        ) : (
          <>
            <div className="h-12 w-12 rounded-xl bg-[#ff6b00]/10 flex items-center justify-center">
              <Upload className="h-6 w-6 text-[#ff6b00]" />
            </div>
            <div className="font-display uppercase font-bold text-slate-200">
              Add Car Photos
            </div>
            <div className="text-xs text-slate-400">
              Front, back, interior, damage — up to {max} shots, 10 MB each
            </div>
          </>
        )}
      </button>
      {error && (
        <div className="mt-2 text-xs text-red-400 font-mono uppercase tracking-wider" data-testid="photo-upload-error">
          {error}
        </div>
      )}

      {photos.length > 0 && (
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
          {photos.map((p, i) => (
            <div
              key={p.id}
              className="relative group rounded-xl overflow-hidden border border-white/10 aspect-square bg-[#1a2234]"
              data-testid={`photo-preview-thumbnail-${i}`}
            >
              <img
                src={photoUrl(p.id)}
                alt="car"
                className="w-full h-full object-cover"
              />
              <button
                type="button"
                onClick={() => removePhoto(p.id)}
                data-testid={`photo-remove-${i}`}
                className="absolute top-2 right-2 h-8 w-8 rounded-full bg-black/70 hover:bg-red-500 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition"
                aria-label="Remove photo"
              >
                <X className="h-4 w-4" />
              </button>
              <div className="absolute bottom-0 left-0 right-0 px-2 py-1 bg-gradient-to-t from-black/80 to-transparent flex items-center gap-1">
                <Camera className="h-3 w-3 text-[#ff6b00]" />
                <span className="font-mono text-[10px] uppercase tracking-wider text-slate-200">
                  Photo {i + 1}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
